import React, { useCallback, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";
import styles from "./ExploreFuture.module.css";
import Image from "next/image";
import Link from "next/link";
import CustomiseCta from "../CustomiseCta/CustomiseCta";
import { ArrowRight } from "lucide-react";

interface ExploreFutureProps {
  data: {
    title_description: {
      title: string;
      description: string;
    };
    learn_more_link_cta?: any;
    explore_your_future_cards: Array<{
      id: number;
      link: {
        label: string;
        url: string;
      };
      image: {
        url: string;
        alternativeText?: any;
      };
    }>;
    mobile_img: {
      url: string;
      id: number;
      documentId: string;
      alternativeText: string | null;
    };
  };
  popUpData?: any;
}

const ExploreFuture: React.FC<ExploreFutureProps> = ({ data, popUpData }) => {
  const swiperRef = useRef<any>(null);
  useEffect(() => {
    const handleResize = () => {};
    if (typeof window !== "undefined") {
      handleResize();
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  useEffect(() => {
    const swiper = swiperRef?.current?.swiper;
    if (swiper) {
      const arrow = document.getElementsByClassName(
        "swiper-button-prev"
      )[0] as HTMLElement;
      arrow.style.display = "none";

      swiper.on("slideChange", () => {
        const realIndex = swiper?.realIndex;
        arrow.style.display = realIndex === 0 ? "none" : "block";
      });

      return () => {
        swiper.destroy(true, true);
      };
    }
  }, []);

  const handleNextSlide = useCallback(
    () => swiperRef.current?.swiper?.slideNext(),
    []
  );
  const handlePrevSlide = useCallback(
    () => swiperRef.current?.swiper?.slidePrev(),
    []
  );

  const MemoizedCustomiseCta = React.memo(CustomiseCta);

  return (
    <div className={styles.talentedComponent} data-section="ExploreFuture">
      <div className={styles.talentedcontent}>
        <div className={styles.leftSection}>
          <div className={styles.title}>
            <h2>{data?.title_description?.title}</h2>
          </div>
          <p className="more-content">{data?.title_description?.description}</p>
          <div>
            <div className={styles.btnStart}>
              <MemoizedCustomiseCta
                ctaData={data?.learn_more_link_cta}
                position="left"
                btnLabelDesktop="Academic Programmes"
                popupData={popUpData}
              />
              <span className="visually-hidden">about Academic Programmes</span>
            </div>
          </div>
        </div>

        <div className={styles.rightSection}>
          <div className={styles.cardContainer}>
            <div
              className={styles.swiperNavigation}
              style={{ minHeight: "50px" }}
            >
              <Swiper
                ref={swiperRef}
                className={`${styles.swiperStyle} swiperStyle`}
                slidesPerView={3}
                spaceBetween={30}
                navigation={{
                  nextEl: ".swiper-button-next",
                  prevEl: ".swiper-button-prev",
                }}
                breakpoints={{
                  960: { slidesPerView: 2.2 },
                  1540: { slidesPerView: 2.6 },
                }}
              >
                {data?.explore_your_future_cards?.map((card, index) => (
                  <SwiperSlide key={card.id}>
                    <div className={styles.Schoolcard}>
                      <Link
                        href={card?.link?.url}
                        aria-label={card?.link?.label}
                        shallow
                        title={card?.link?.label}
                      >
                        <div className={styles.imageWrapper}>
                          <Image
                            src={card?.image?.url}
                            alt={card?.image?.alternativeText || "School-cards"}
                            title={
                              card?.image?.alternativeText || "School-cards"
                            }
                            width={300}
                            height={390}
                            sizes="(max-width: 768px) 320px, 640px"
                            quality={80}
                            style={{
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                            className={styles.cardImage}
                            priority={index === 0}
                            loading={index === 0 ? "eager" : "lazy"}
                          />
                          <div className={styles.cardContentContainer}>
                            <div className={styles.cardContent}>
                              <p className={styles.cardText}>
                                {card?.link?.label}
                              </p>
                              <ArrowRight className={styles.arrowIcon} />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <button
                aria-label="Next Slide"
                className={`swiper-button-next ${styles.nextButton}`}
                onClick={handleNextSlide}
              ></button>

              <button
                aria-label="Previous Slide"
                className={`swiper-button-prev ${styles.prevButton}`}
                onClick={handlePrevSlide}
              ></button>
            </div>
          </div>
        </div>

        <div className={styles.MobileExplore}>
          <div className={styles.ExploreImg}>
            <Image
              src={data?.mobile_img?.url}
              alt={data?.mobile_img?.alternativeText || "schools"}
              title={data?.mobile_img?.alternativeText || "schools"}
              width={320}
              height={415}
              style={{
                width: "100%",
                height: "auto",
                maxWidth: "320px",
              }}
              sizes="(max-width: 768px) 90vw, 320px"
              quality={80}
              priority
              loading="eager"
              fetchPriority="high"
            />
          </div>
          <div>
            {data?.explore_your_future_cards?.map((card, index) => (
              <div key={card?.id}>
                <Link
                  href={card?.link?.url}
                  aria-label={card?.link?.label}
                  shallow
                  title={card?.link?.label}
                >
                  <div
                    className={`${styles.ExploreCard} ${
                      index === data?.explore_your_future_cards?.length - 1
                        ? ""
                        : styles.bottomBorder
                    }`}
                  >
                    <div className={styles.ExploreCardIcon}>
                      <ArrowRight />
                    </div>
                    <p className={styles.cardText}>{card?.link?.label}</p>
                  </div>
                </Link>
              </div>
            ))}
            <div>
              <div className={styles.btnStartMobile}>
                <MemoizedCustomiseCta
                  ctaData={data?.learn_more_link_cta}
                  position="left"
                  btnLabelDesktop="Academic Programmes"
                  popupData={popUpData}
                />
                <span className="visually-hidden">
                  about Academic Programmes
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreFuture;
